import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, CheckBox, Typography } from "../../../../components";
import { useTranslation } from "react-i18next";
import { PermissionItem, PermissionsByCategory } from "../types";

type PermissionsSelectorProps = {
  permissions: PermissionsByCategory;
  effectivePermissions?: Record<string, boolean>;
  disabled?: boolean;
  onChange?: (updated: PermissionsByCategory) => void;
};

const PermissionsSelector: FC<PermissionsSelectorProps> = ({
  permissions,
  effectivePermissions,
  disabled,
  onChange,
}) => {
  const [t] = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  const filterTokens = useMemo(
    () =>
      searchValue
        .toLowerCase()
        .split(" ")
        .map((token) => token.trim())
        .filter(Boolean),
    [searchValue]
  );

  const filteredPermissions = useMemo(() => {
    if (!filterTokens.length) {
      return permissions || {};
    }

    const result: PermissionsByCategory = {};

    Object.entries(permissions || {}).forEach(([category, perms]) => {
      const matches = perms.filter((permission) => {
        const valuesToSearch = [
          permission.name,
          permission.display_name,
          permission.description,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());

        if (permission.keywords) {
          const keywordValues = Array.isArray(permission.keywords)
            ? permission.keywords
            : Object.values(permission.keywords).flat();
          keywordValues.forEach((keyword) =>
            valuesToSearch.push(String(keyword).toLowerCase())
          );
        }

        return filterTokens.every((token) =>
          valuesToSearch.some((value) => value.includes(token))
        );
      });

      if (matches.length) {
        result[category] = matches;
      }
    });

    return result;
  }, [permissions, filterTokens]);

  const filteredCategories = useMemo(
    () => Object.keys(filteredPermissions || {}),
    [filteredPermissions]
  );

  useEffect(() => {
    if (!filteredCategories.length) {
      setActiveCategory("");
      return;
    }

    if (!activeCategory || !filteredCategories.includes(activeCategory)) {
      setActiveCategory(filteredCategories[0]);
    }
  }, [filteredCategories, activeCategory]);

  const currentCategory = filteredPermissions?.[activeCategory] || [];
  const sourceCategory = permissions?.[activeCategory] || [];

  const splitPermissions = useMemo(() => {
    const midPoint = Math.ceil(currentCategory.length / 2);
    return [
      currentCategory.slice(0, midPoint),
      currentCategory.slice(midPoint),
    ];
  }, [currentCategory]);

  const handleToggle = (permissionName: string) => {
    if (!activeCategory || !onChange || disabled) {
      return;
    }

    const updatedCategory = sourceCategory.map((permission: PermissionItem) =>
      permission.name === permissionName
        ? { ...permission, granted: !permission.granted }
        : permission
    );

    onChange({
      ...permissions,
      [activeCategory]: updatedCategory,
    });
  };

  const handleBulkChange = (granted: boolean) => {
    if (!activeCategory || !onChange || disabled) {
      return;
    }

    const visibleNames = new Set(
      (filteredPermissions?.[activeCategory] || currentCategory).map(
        (permission) => permission.name
      )
    );

    const updatedCategory = sourceCategory.map((permission: PermissionItem) =>
      visibleNames.has(permission.name)
        ? { ...permission, granted }
        : permission
    );

    onChange({
      ...permissions,
      [activeCategory]: updatedCategory,
    });
  };

  return (
    <SelectorContainer>
      <SearchRow>
        <SearchField
          value={searchValue}
          placeholder={t("AUTHORIZATION_SEARCH_PERMISSIONS")}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <ButtonsRow>
          <Button
            title={t("PERMISSIONS_SELECT_ALL")}
            size="small"
            variant="stroke"
            disabled={!currentCategory.length || disabled}
            onClick={() => handleBulkChange(true)}
          />
          <Button
            title={t("PERMISSIONS_DESELECT_ALL")}
            size="small"
            variant="secondary"
            disabled={!currentCategory.length || disabled}
            onClick={() => handleBulkChange(false)}
          />
        </ButtonsRow>
      </SearchRow>

      <PermissionsContent>
        <CategoryList>
          {filteredCategories.map((category) => (
            <CategoryItem
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              $active={activeCategory === category}
            >
              <Typography variant="body">{category}</Typography>
            </CategoryItem>
          ))}
          {!filteredCategories.length && (
            <MutedText>{t("AUTHORIZATION_NO_PERMISSIONS_FOUND")}</MutedText>
          )}
        </CategoryList>

        <PermissionsColumns>
          {splitPermissions.map((column, columnIndex) => (
            <PermissionColumn key={String(columnIndex)}>
              {column.map((permission) => (
                <PermissionRow key={permission.name}>
                  <PermissionText>
                    <PermissionTitle>{permission.display_name}</PermissionTitle>
                    {permission.description && (
                      <PermissionDescription>
                        {permission.description}
                      </PermissionDescription>
                    )}
                  </PermissionText>
                  <PermissionToggles>
                    <CheckBox
                      title=""
                      value={permission.granted}
                      disabled={disabled}
                      onChange={() => handleToggle(permission.name)}
                    />
                    {effectivePermissions && (
                      <EffectiveBadge
                        title={t("AUTHORIZATION_EFFECTIVE_PERMISSION")}
                        data-active={
                          effectivePermissions[permission.name] ? "true" : "false"
                        }
                        $active={!!effectivePermissions[permission.name]}
                      >
                        {effectivePermissions[permission.name]
                          ? t("ACTIVE")
                          : t("DISABLED")}
                      </EffectiveBadge>
                    )}
                  </PermissionToggles>
                </PermissionRow>
              ))}
            </PermissionColumn>
          ))}
        </PermissionsColumns>
      </PermissionsContent>
    </SelectorContainer>
  );
};

export default PermissionsSelector;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SearchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchField = styled.input`
  min-width: 260px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.grey[350]};
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  color: ${({ theme }) => theme.grey[800]};
  background: ${({ theme }) => theme.base.white};

  &:focus {
    border-color: ${({ theme }) => theme.primary[500]};
    box-shadow: 0 0 0 3px rgba(15, 104, 233, 0.12);
  }
`;

const PermissionsContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
`;

const CategoryItem = styled.button<{ $active: boolean }>`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ $active, theme }) =>
    $active ? theme.primary[25] : theme.base.white};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.primary[200] : theme.grey[350])};
  color: ${({ $active, theme }) =>
    $active ? theme.primary[700] : theme.grey[800]};
  text-align: left;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    cursor: pointer;
    border-color: ${({ theme }) => theme.primary[300]};
  }
`;

const PermissionsColumns = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  min-width: 320px;
  flex-wrap: wrap;
`;

const PermissionColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
`;

const PermissionRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.grey[350]};
  background: ${({ theme }) => theme.base.white};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
`;

const PermissionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PermissionTitle = styled.div`
  color: ${({ theme }) => theme.grey[900]};
  font-weight: 600;
  font-size: 14px;
`;

const PermissionDescription = styled.div`
  color: ${({ theme }) => theme.grey[700]};
  font-size: 13px;
  line-height: 18px;
`;

const PermissionToggles = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EffectiveBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $active, theme }) =>
    $active ? theme.primary[700] : theme.grey[700]};
  background: ${({ $active, theme }) =>
    $active ? theme.primary[25] : theme.grey[200]};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.primary[100] : theme.grey[350]};
`;

const MutedText = styled.div`
  color: ${({ theme }) => theme.grey[700]};
  font-size: 13px;
`;
